import { createClient } from '@/lib/supabase/client';

// Lazily-initialized Supabase browser client. Constructed on FIRST USE (not at
// module load) so importing this module in a non-browser context (e.g. a node
// vitest env without NEXT_PUBLIC_SUPABASE_* vars) doesn't throw — the browser
// client constructor requires those env vars. The auth-state listener that
// clears the cache is registered once, alongside construction.
let supabaseClient: ReturnType<typeof createClient> | null = null;

function getSupabase(): ReturnType<typeof createClient> {
  if (!supabaseClient) {
    supabaseClient = createClient();
    // Auto-clear cache on auth state changes
    supabaseClient.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        clearAuthCache();
      }
    });
  }
  return supabaseClient;
}

// sessionStorage key for the active impersonation state. Single source of truth —
// impersonation-context.tsx imports this instead of re-declaring the literal (#584).
export const IMPERSONATION_STORAGE_KEY = 'sentrial-impersonation';

// Cached session token with expiration
let cachedSession: { token: string | null; expiresAt: number } = { token: null, expiresAt: 0 };

/**
 * Get cached auth token - avoids repeated getSession() calls
 * Caches token until 60 seconds before expiration
 */
export async function getAuthToken(): Promise<string | null> {
  const now = Date.now();

  // Return cached token if still valid (with 60s buffer)
  if (cachedSession.token && cachedSession.expiresAt > now + 60000) {
    return cachedSession.token;
  }

  // Fetch fresh session
  const {
    data: { session },
  } = await getSupabase().auth.getSession();

  if (session?.access_token) {
    cachedSession = {
      token: session.access_token,
      expiresAt: session.expires_at ? session.expires_at * 1000 : now + 3600000,
    };
    return session.access_token;
  }

  return null;
}

/**
 * Clear the cached session - called internally on sign out (the auth state listener above) or
 * token refresh. Not exported: no external caller today.
 */
function clearAuthCache(): void {
  cachedSession = { token: null, expiresAt: 0 };
}

export function getImpersonationUserId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const stored = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed?.targetUserId || null;
    }
  } catch {
    // Ignore
  }
  return null;
}
